// StatStrip — a compact, read-only row of at-a-glance counts (number over label),
// for an entity peek / detail "contents" band. Reusable across dashboards
// (a zone's sites/warehouses/pools; later a cluster's, a warehouse's stock…).
//
// Deliberately NOT a property list with dividers (that reads heavy and, beside an
// editable card, implies the numbers are editable). These are derived facts —
// presentation only, no edit affordance. A future drill-through (click a count →
// the filtered list) lands when those lists exist; the value slot already takes a
// node so a link drops in without an API change.

import type { ReactNode } from 'react';
import { HScroll } from '../ui/HScroll.js';

export interface Stat {
  label: string;
  value: ReactNode;
}

export interface StatStripProps {
  stats: Stat[];
  /** Wrap the row in a bordered surface with horizontal overflow — the hero KPI
   *  band on a detail PAGE, where the strip otherwise floats bare on the page
   *  canvas. A peek / rail already provides a surface, so it stays UNFRAMED there.
   *  Framed → the stats scroll horizontally (they never wrap or break). */
  framed?: boolean;
  className?: string;
}

export function StatStrip({ stats, framed = false, className = '' }: StatStripProps) {
  const items = stats.map((s) => (
    <div key={s.label} className="flex shrink-0 flex-col">
      <span className="text-lg font-semibold tabular-nums text-[var(--color-text-primary)]">{s.value}</span>
      <span className="text-xs text-[var(--color-text-secondary)]">{s.label}</span>
    </div>
  ));
  if (framed) {
    // Horizontal overflow rides HScroll (hidden bar + edge-fade) — the app's strip
    // pattern — so a scrollbar never sits over the labels; the fade blends to the
    // card surface.
    return (
      <div className={`rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 ${className}`}>
        <HScroll className="flex gap-x-10" fadeColor="var(--color-bg-surface)">
          {items}
        </HScroll>
      </div>
    );
  }
  return <div className={`flex flex-wrap gap-x-8 gap-y-3 ${className}`}>{items}</div>;
}
